/**
 * Token-bound monograph narrative composites.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { ArrowRight, Dot } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographFeatureGridProps,
  MonographInlineCodeProps,
  MonographPanelTokens,
  MonographStepListProps,
} from "./MonographPanelPrimitiveTypes.js";

const featureClass = cva("csk-monograph-feature-grid__item", {
  variants: {
    variant: {
      cards: "csk-monograph-feature-grid__item--card",
      rows: "csk-monograph-feature-grid__item--row",
    },
  },
  defaultVariants: { variant: "cards" },
});

interface NarrativeFactoryInput {
  readonly tokens: MonographPanelTokens;
}

type MotionRevealProps =
  | { readonly initial: false }
  | {
      readonly animate: { readonly opacity: number; readonly y: number };
      readonly initial: { readonly opacity: number; readonly y: number };
      readonly transition: { readonly delay: number; readonly duration: number };
    };

function itemMotion(index: number, reducedMotion: boolean): MotionRevealProps {
  return reducedMotion
    ? { initial: false as const }
    : {
        animate: { opacity: 1, y: 0 },
        initial: { opacity: 0, y: 6 },
        transition: { delay: index * 0.025, duration: 0.16 },
      };
}

function renderBadges(
  badges: MonographFeatureGridProps["items"][number]["badges"],
  tokens: MonographPanelTokens,
): React.ReactNode {
  if (!badges?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {badges.map((badge, index) => {
        const color = badge.color ?? tokens.accent;
        return (
          <span
            key={badge.id ?? String(index)}
            title={badge.title}
            style={{
              background: colorWithAlpha(color, "18"),
              border: `1px solid ${colorWithAlpha(color, "55")}`,
              borderRadius: 999,
              color,
              display: "inline-flex",
              fontFamily: tokens.mono,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0,
              lineHeight: 1,
              padding: "4px 7px",
              whiteSpace: "nowrap",
            }}
          >
            {badge.label}
          </span>
        );
      })}
    </div>
  );
}

export function createMonographPanelNarrative({ tokens }: NarrativeFactoryInput): {
  readonly FeatureGrid: (props: MonographFeatureGridProps) => React.JSX.Element;
  readonly InlineCode: (props: MonographInlineCodeProps) => React.JSX.Element;
  readonly StepList: (props: MonographStepListProps) => React.JSX.Element;
} {
  function InlineCode({
    children,
    color = tokens.bright,
    className,
    style,
  }: MonographInlineCodeProps): React.JSX.Element {
    return (
      <code
        className={cn("csk-monograph-inline-code", className)}
        style={{
          background: "rgba(7,6,13,0.55)",
          border: `1px solid ${tokens.border}`,
          borderRadius: 6,
          color,
          fontFamily: tokens.mono,
          fontSize: "0.92em",
          padding: "1px 6px",
          ...style,
        }}
      >
        {children}
      </code>
    );
  }

  function FeatureGrid({
    items,
    ariaLabel = "Feature grid",
    variant = "cards",
    minColumnWidth = 240,
    gap = 12,
    className,
  }: MonographFeatureGridProps): React.JSX.Element {
    const reducedMotion = useReducedMotion() ?? false;
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn("csk-monograph-feature-grid", className)}
        style={{
          display: "grid",
          gap,
          gridTemplateColumns:
            variant === "rows"
              ? "minmax(0, 1fr)"
              : `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
        }}
      >
        {items.map((item, index) => {
          const color = item.color ?? tokens.accent;
          return (
            <motion.article
              key={item.id}
              role="listitem"
              className={featureClass({ variant })}
              {...itemMotion(index, reducedMotion)}
              style={{
                background:
                  variant === "rows"
                    ? `linear-gradient(135deg, ${colorWithAlpha(color, "10")}, transparent 82%)`
                    : colorWithAlpha(color, "0d"),
                border: `1px solid ${colorWithAlpha(color, "33")}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 12,
                display: "grid",
                gap: variant === "rows" ? 12 : 9,
                gridTemplateColumns: variant === "rows" ? "minmax(0, 1fr) auto" : undefined,
                padding: variant === "rows" ? "13px 15px" : "14px 16px",
              }}
            >
              <div style={{ minWidth: 0 }}>
                {item.eyebrow ? (
                  <div
                    style={{
                      color,
                      fontFamily: tokens.mono,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0,
                      marginBottom: 5,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.eyebrow}
                  </div>
                ) : null}
                <div style={{ color: tokens.bright, fontSize: 14, fontWeight: 800 }}>
                  {item.title}
                </div>
                <div style={{ color: tokens.text, fontSize: 13, lineHeight: 1.62, marginTop: 6 }}>
                  {item.description}
                </div>
                {item.footer ? (
                  <div style={{ color: tokens.muted, fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
                    {item.footer}
                  </div>
                ) : null}
              </div>
              {item.badges?.length || item.meta ? (
                <div
                  style={{
                    alignItems: variant === "rows" ? "flex-end" : "flex-start",
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                    justifyContent: "flex-start",
                    minWidth: variant === "rows" ? 160 : undefined,
                    textAlign: variant === "rows" ? "right" : "left",
                  }}
                >
                  {item.meta ? (
                    <span style={{ color: tokens.muted, fontFamily: tokens.mono, fontSize: 11 }}>
                      {item.meta}
                    </span>
                  ) : null}
                  {renderBadges(item.badges, tokens)}
                </div>
              ) : null}
            </motion.article>
          );
        })}
      </div>
    );
  }

  function StepList({
    items,
    ariaLabel = "Steps",
    dense = false,
    className,
  }: MonographStepListProps): React.JSX.Element {
    const reducedMotion = useReducedMotion() ?? false;
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn("csk-monograph-step-list", className)}
        style={{ display: "grid", gap: dense ? 8 : 10 }}
      >
        {items.map((item, index) => {
          const color = item.color ?? tokens.accent;
          return (
            <motion.div
              key={item.id}
              role="listitem"
              {...itemMotion(index, reducedMotion)}
              style={{
                alignItems: "flex-start",
                background: colorWithAlpha(color, "0d"),
                border: `1px solid ${colorWithAlpha(color, "30")}`,
                borderRadius: 12,
                display: "flex",
                gap: 12,
                padding: dense ? "10px 12px" : "12px 14px",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  alignItems: "center",
                  background: colorWithAlpha(color, "1c"),
                  border: `1px solid ${colorWithAlpha(color, "55")}`,
                  borderRadius: 9,
                  color,
                  display: "inline-flex",
                  flexShrink: 0,
                  fontFamily: tokens.mono,
                  fontSize: 12,
                  fontWeight: 800,
                  height: 28,
                  justifyContent: "center",
                  width: 28,
                }}
              >
                {index + 1}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <strong style={{ color: tokens.bright, fontSize: 13.5 }}>{item.title}</strong>
                  {item.meta ? (
                    <>
                      <ArrowRight aria-hidden="true" color={tokens.muted} size={13} />
                      <span style={{ color: tokens.muted, fontFamily: tokens.mono, fontSize: 11 }}>
                        {item.meta}
                      </span>
                    </>
                  ) : null}
                </div>
                {item.description ? (
                  <div
                    style={{ color: tokens.text, fontSize: 12.8, lineHeight: 1.6, marginTop: 4 }}
                  >
                    <Dot
                      aria-hidden="true"
                      size={14}
                      style={{ marginLeft: -4, verticalAlign: -2 }}
                    />
                    {item.description}
                  </div>
                ) : null}
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  }

  return { FeatureGrid, InlineCode, StepList };
}
